import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Check,
  ChevronRight,
  ChevronsUpDown,
  Database,
  GitPullRequest,
  Info,
  LayoutDashboard,
  Lightbulb,
  ListFilter,
  LoaderCircle,
  MessageSquare,
  Radio,
  ReceiptText,
  RefreshCw,
  Search,
  StickyNote,
  Workflow,
} from 'lucide-react';
import { type ComponentType, useCallback, useMemo, useState } from 'react';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/_shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Input } from '@/_shared/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { formatDate, formatDateTimeTitle } from '@/_shared/lib/format';
import { client } from '@/_shared/lib/rpc';
import { cn } from '@/_shared/lib/utils';

dayjs.extend(utc);

// A base icon with a small refresh badge overlaid at the bottom-right corner —
// used for "refreshed" actions that re-index an existing source/warehouse.
function withRefreshBadge(Base: ComponentType<{ className?: string }>) {
  return function RefreshBadgeIcon({ className }: { className?: string }) {
    return (
      <span className={cn('relative inline-flex', className)}>
        <Base className="size-full" />
        <RefreshCw className="absolute -right-0.5 -bottom-0.5 size-[60%] rounded-full bg-background" strokeWidth={2} />
      </span>
    );
  };
}

// Icons are a presentation concern, so they stay in the frontend — keyed by the
// backend action type. Everything else (label, noun, credits, rationale) comes
// from the `credits.pricingTable` RPC, which reads the code config in src/lib/credits-pricing.ts.
// `pr_raised` / `source_indexed` / `source_refreshed` are retired actions (ACT2-414) kept so
// historical usage rows still render their original icon rather than falling back.
// `skill_note_created` is the pre-split notes+skills meter (retired by rename).
const ACTION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  pr_raised: GitPullRequest,
  source_indexed: Radio,
  source_refreshed: withRefreshBadge(Radio),
  warehouse_indexed: Database,
  warehouse_refreshed: withRefreshBadge(Database),
  dashboard_created: LayoutDashboard,
  routine_created: Workflow,
  skill_note_created: StickyNote,
  note_created: StickyNote,
  skill_created: Lightbulb,
  chat_thread_opened: MessageSquare,
};

// Each action keeps a unique, reserved categorical color (like the chart palette) so the
// breakdown bar, legend, and dropdown dots stay stable even for actions with zero usage.
const ACTION_COLORS: Record<string, string> = {
  pr_raised: '#d06a3f',
  source_indexed: '#1f9c93',
  source_refreshed: '#4cb89a',
  warehouse_indexed: '#2f6fd6',
  warehouse_refreshed: '#5aa0e0',
  dashboard_created: '#6c5cd0',
  routine_created: '#c2557e',
  skill_note_created: '#c0852a',
  note_created: '#c0852a',
  skill_created: '#d4a017',
  chat_thread_opened: '#8b94a6',
};
const FALLBACK_COLOR = '#818da0';

function actionColor(action: string): string {
  return ACTION_COLORS[action] ?? FALLBACK_COLOR;
}

// Derived from the RPC output so the shape stays in lockstep with the server — no manual drift, no casts.
type PricingItem = Awaited<ReturnType<typeof client.credits.pricingTable>>['items'][number];
type UsageRow = Awaited<ReturnType<typeof client.credits.listUsage>>['items'][number];
type ActionMeta = Record<string, { label: string; noun: string; credits: number; rationale: string }>;

function monthStart(p: { year: number; month: number }): dayjs.Dayjs {
  return dayjs.utc(`${p.year}-${String(p.month).padStart(2, '0')}-01`);
}

function periodKey(p: { year: number; month: number }): string {
  return monthStart(p).format('YYYY-MM');
}

function monthLabel(p: { year: number; month: number }): string {
  return monthStart(p).format('MMMM YYYY');
}

function currentPeriod(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

export function SettingsUsage(): React.ReactElement {
  usePageTitle('Usage — Settings');
  const orpc = useOrpc();
  const { slug, isAdmin } = useWorkspace();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // One shared filter ('all' or an action) drives the breakdown, the dropdown, and the table together.
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const monthsQuery = useQuery(orpc.credits.months.queryOptions({ input: {} }));
  const pricingQuery = useQuery(orpc.credits.pricingTable.queryOptions({ input: {} }));

  const monthOptions = useMemo(() => monthsQuery.data?.months ?? [], [monthsQuery.data]);
  // Effective selected period: user choice, else newest month with data, else the current month.
  const period = useMemo(() => {
    if (selectedKey) {
      const found = monthOptions.find((m) => periodKey(m) === selectedKey);
      if (found) return found;
    }
    return monthOptions[0] ?? currentPeriod();
  }, [selectedKey, monthOptions]);

  const summaryQuery = useQuery(orpc.credits.summary.queryOptions({ input: period }));
  const byActionQuery = useQuery(orpc.credits.usageByAction.queryOptions({ input: period }));

  // `retiredItems` (pr_raised/source_indexed/source_refreshed, ACT2-414) is folded in here purely so a
  // historical usage row resolves a real label/noun instead of falling back to the raw action id —
  // it must NOT reach the rendered price list or the action-filter dropdown, both of which read
  // `pricingQuery.data?.items` directly (not this map).
  const metaByAction: ActionMeta = useMemo(() => {
    const out: ActionMeta = {};
    for (const item of [...(pricingQuery.data?.items ?? []), ...(pricingQuery.data?.retiredItems ?? [])]) {
      out[item.action] = { label: item.label, noun: item.noun, credits: item.credits, rationale: item.rationale };
    }
    return out;
  }, [pricingQuery.data]);

  const metaFor = useCallback(
    (action: string): { label: string; noun: string; credits: number; rationale: string } =>
      metaByAction[action] ?? { label: action, noun: 'item', credits: 0, rationale: '' },
    [metaByAction],
  );

  const groups = byActionQuery.data?.groups ?? [];
  const monthTotal = byActionQuery.data?.total ?? summaryQuery.data?.monthCredits ?? 0;
  const yearTotal = summaryQuery.data?.yearCredits ?? 0;
  const totalActions = groups.reduce((sum, g) => sum + g.count, 0);

  // Activity log (one month, keyset-paginated newest-first) — admins/owners only. Filtering by the
  // shared action filter + free-text search is applied client-side over the loaded pages.
  const detailQuery = useInfiniteQuery({
    queryKey: [slug, 'credits', 'listUsage', period.year, period.month],
    queryFn: ({ pageParam }) =>
      client.credits.listUsage({ year: period.year, month: period.month, limit: 100, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: isAdmin,
  });

  const allRows = useMemo(() => (detailQuery.data?.pages ?? []).flatMap((p) => p.items), [detailQuery.data]);
  const detailRows = useMemo(() => {
    return allRows
      .filter((e) => (filter === 'all' ? true : e.action === filter))
      .filter((e) => {
        if (search.trim() === '') return true;
        const label = metaFor(e.action).label;
        return `${e.detail ?? ''} ${e.who} ${label}`.toLowerCase().includes(search.toLowerCase());
      });
  }, [allRows, filter, search, metaFor]);

  function toggleFilter(action: string): void {
    setFilter((prev) => (prev === action ? 'all' : action));
  }

  const filterLabel = filter === 'all' ? 'All actions' : metaFor(filter).label;
  const hasSearch = search.trim() !== '';
  // Filtered-by-action shows loaded-of-that-action's-total; a search can't know its true total, so it reports only loaded matches.
  const filterTotal = groups.find((g) => g.action === filter)?.count ?? 0;
  const countLabel = hasSearch
    ? `Showing ${detailRows.length} match${detailRows.length === 1 ? '' : 'es'}`
    : filter === 'all'
      ? `${totalActions} actions this month`
      : `Showing ${detailRows.length} of ${filterTotal}`;

  const activityColumns = useMemo<TableColumn<UsageRow>[]>(
    () => [
      {
        id: 'action',
        header: 'Action',
        cell: (e) => {
          const Icon = ACTION_ICONS[e.action] ?? MessageSquare;
          return (
            <span className="flex min-w-0 items-start gap-2">
              <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0">
                <span className="block truncate text-foreground" title={metaFor(e.action).label}>
                  {metaFor(e.action).label}
                </span>
                {e.detail && (
                  <span className="block truncate text-xs text-muted-foreground" title={e.detail}>
                    {e.detail}
                  </span>
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'who',
        header: 'Who',
        width: '10rem',
        cell: (e) => (
          <span className="block truncate text-muted-foreground" title={e.who}>
            {e.who}
          </span>
        ),
      },
      {
        id: 'when',
        header: 'When',
        width: '7rem',
        cell: (e) => (
          <span className="block truncate text-muted-foreground" title={formatDateTimeTitle(e.occurredAt)}>
            {formatDate(e.occurredAt)}
          </span>
        ),
      },
      {
        id: 'credits',
        header: 'Credits',
        align: 'right',
        width: '4rem',
        cell: (e) => <span className="font-medium tabular-nums">{e.credits ?? '—'}</span>,
      },
    ],
    [metaFor],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">AI Credits</h2>
        <div className="flex items-center gap-2">
          <Select
            value={periodKey(period)}
            onValueChange={(value) => {
              setSelectedKey(value);
              // Reset the shared action filter + search on month change — otherwise switching to a month
              // lacking the filtered action strands the panel dimmed at "Showing 0 of 0" despite real usage.
              setFilter('all');
              setSearch('');
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={periodKey(m)} value={periodKey(m)}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <HowCreditsWork />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Stat label={monthLabel(period)} value={monthTotal} hint="credits used this month" emphasis />
        <Stat label="This year" value={yearTotal} hint={`credits used in ${period.year}`} />
      </div>

      <section className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
              Where your credits went
            </h3>
            {isAdmin && (
              <p className="mt-0.5 text-xs text-muted-foreground">Click an action to filter the activity log below.</p>
            )}
          </div>
          <PricingTableDialog items={pricingQuery.data?.items ?? []} />
        </div>

        {byActionQuery.isError ? (
          <UsageLoadError message="Couldn't load usage." onRetry={() => void byActionQuery.refetch()} />
        ) : byActionQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : groups.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted-foreground">
            No credits used in {monthLabel(period)}.
          </p>
        ) : (
          <>
            <div className="px-5">
              <div className="flex h-3.5 gap-0.5 overflow-hidden rounded-full bg-muted">
                {groups.map((g) => {
                  const pct = monthTotal === 0 ? 0 : (g.credits / monthTotal) * 100;
                  const dim = filter !== 'all' && g.action !== filter;
                  const segTitle = `${metaFor(g.action).label} · ${g.credits.toLocaleString()} credits (${Math.round(pct)}%)`;
                  const segStyle = {
                    width: `${pct}%`,
                    backgroundColor: actionColor(g.action),
                    opacity: dim ? 0.28 : 1,
                  };
                  // Segments filter the activity log, so they're interactive only for admins (who can see it).
                  return isAdmin ? (
                    <button
                      key={g.action}
                      type="button"
                      onClick={() => toggleFilter(g.action)}
                      title={segTitle}
                      aria-label={`Filter by ${metaFor(g.action).label}`}
                      className="h-full min-w-1 transition-opacity"
                      style={segStyle}
                    />
                  ) : (
                    <div key={g.action} title={segTitle} className="h-full min-w-1" style={segStyle} />
                  );
                })}
              </div>
            </div>

            <ul className="space-y-0.5 px-3 pt-3 pb-3">
              {groups.map((g) => {
                const meta = metaFor(g.action);
                const Icon = ACTION_ICONS[g.action] ?? MessageSquare;
                const pct = monthTotal === 0 ? 0 : Math.round((g.credits / monthTotal) * 100);
                const active = filter === g.action;
                const dim = filter !== 'all' && !active;
                // Rows filter the activity log, so they're only interactive for admins (who can see it).
                const rowBody = (
                  <>
                    <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: actionColor(g.action) }} />
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{meta.label}</span>
                    <span className="shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                      {g.count.toLocaleString()} {g.count === 1 ? meta.noun : `${meta.noun}s`}
                    </span>
                    <span className="w-12 shrink-0 text-right text-sm font-semibold text-foreground tabular-nums">
                      {g.credits.toLocaleString()}
                    </span>
                    <span className="w-10 shrink-0 text-right text-xs text-muted-foreground tabular-nums">{pct}%</span>
                  </>
                );
                return (
                  <li key={g.action}>
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => toggleFilter(g.action)}
                        aria-pressed={active}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-2.5 py-2.5 text-left transition-colors',
                          active
                            ? 'bg-sidebar-accent font-medium text-primary'
                            : 'hover:bg-grey-75 hover:text-foreground',
                          dim && 'opacity-55',
                        )}
                      >
                        {rowBody}
                        <ChevronRight
                          className={cn('size-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground/40')}
                        />
                      </button>
                    ) : (
                      <div className="flex w-full items-center gap-3 rounded-md px-2.5 py-2.5">{rowBody}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* Activity log — admins/owners only, driven by the shared filter + search */}
      {isAdmin && (
        <section className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
            <div>
              <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Activity log</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{countLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search activity…"
                  className="w-44 pl-8"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" className="w-48 justify-between gap-2">
                    <span className="flex items-center gap-2 truncate">
                      <ListFilter className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{filterLabel}</span>
                    </span>
                    <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-60">
                  <ActionFilterOption label="All actions" active={filter === 'all'} onSelect={() => setFilter('all')} />
                  {(pricingQuery.data?.items ?? []).map((item) => (
                    <ActionFilterOption
                      key={item.action}
                      label={item.label}
                      color={actionColor(item.action)}
                      active={filter === item.action}
                      onSelect={() => setFilter(item.action)}
                    />
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {detailQuery.isError ? (
            <UsageLoadError message="Couldn't load the activity log." onRetry={() => void detailQuery.refetch()} />
          ) : detailQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table
              data={detailRows}
              columns={activityColumns}
              getRowId={(e) => e.id}
              emptyState="No matching activity."
              bordered={false}
              fixedLayout
              // Infinite-scroll "load more" appends pages — keep the scroll position instead of snapping to top.
              resetScrollOnDataChange={false}
            />
          )}

          {detailQuery.hasNextPage && (
            <div className="border-t border-border px-5 py-3 text-center">
              <button
                type="button"
                onClick={() => void detailQuery.fetchNextPage()}
                disabled={detailQuery.isFetchingNextPage}
                className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
              >
                {detailQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </section>
      )}

      <p className="text-xs text-muted-foreground">
        Credits measure AI usage. They are not a dollar amount — pricing is set at the RudderStack account level.
      </p>
    </div>
  );
}

// A failed usage RPC must read as a load error, not a real "no usage" zero state on a billing page.
function UsageLoadError({ message, onRetry }: { message: string; onRetry: () => void }): React.ReactElement {
  return (
    <Callout variant="error" className="m-5">
      <div className="flex items-center justify-between gap-3">
        <span>{message}</span>
        <Button type="button" variant="secondary" className="shrink-0" label="Retry" onClick={onRetry} />
      </div>
    </Callout>
  );
}

function ActionFilterOption({
  label,
  color,
  active,
  onSelect,
}: {
  label: string;
  color?: string;
  active: boolean;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <DropdownMenuItem onSelect={onSelect} className="justify-between gap-2">
      <span className="flex items-center gap-2">
        {color && <span className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: color }} />}
        {label}
      </span>
      {active && <Check className="size-4 text-primary" />}
    </DropdownMenuItem>
  );
}

function Stat({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: number;
  hint: string;
  emphasis?: boolean;
}): React.ReactElement {
  return (
    <div className={cn('rounded-xl border border-border p-5', emphasis ? 'bg-muted' : 'bg-card')}>
      <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{label}</p>
      <p className="mt-1 text-3xl font-semibold tracking-[-0.02em] text-foreground tabular-nums">
        {value.toLocaleString()}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function HowCreditsWork(): React.ReactElement {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" className="gap-1.5">
          <Info className="size-3.5" />
          What's a credit?
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" align="end">
        <p className="font-medium text-foreground">Credits = AI usage</p>
        <p className="mt-2 text-muted-foreground">
          Each action the AI does for you costs a fixed number of credits — opening a chat, creating a dashboard,
          raising a PR, indexing a source. You see actions, not tokens.
        </p>
        <p className="mt-2 text-muted-foreground">
          Credits are <span className="font-medium text-foreground">not a dollar amount</span>. They measure how much AI
          you've used; pricing is handled at the RudderStack account level.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function PricingTableDialog({ items }: { items: PricingItem[] }): React.ReactElement {
  // Memoized so the Table sees one stable `data` identity — a fresh array per render reads as "new result set".
  const rows = useMemo(() => [...items].sort((a, b) => b.credits - a.credits), [items]);
  const columns: TableColumn<PricingItem>[] = [
    {
      id: 'label',
      header: 'Action',
      width: '14rem',
      cell: (item) => {
        const Icon = ACTION_ICONS[item.action] ?? MessageSquare;
        return (
          <span className="flex min-w-0 items-center gap-2 text-foreground">
            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate" title={item.label}>
              {item.label}
            </span>
          </span>
        );
      },
    },
    {
      id: 'credits',
      header: 'Credits',
      align: 'right',
      width: '6rem',
      cell: (item) => <span className="font-semibold tabular-nums">{item.credits}</span>,
    },
    {
      id: 'rationale',
      header: 'What you get',
      wrap: true,
      cell: (item) => <span className="text-xs text-muted-foreground">{item.rationale}</span>,
    },
  ];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" className="gap-1.5">
          <ReceiptText className="size-3.5" />
          View credit costs
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Credit cost per action</DialogTitle>
          <DialogDescription>How many credits each AI action consumes.</DialogDescription>
        </DialogHeader>
        <Table data={rows} columns={columns} getRowId={(item) => item.action} fixedLayout />
      </DialogContent>
    </Dialog>
  );
}
